import 'swiper/css';
import 'swiper/css/navigation';

import { ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { radius } from '@/styles/sizes';

import DashboardItem from '../Dashboard/components/DashboardItem';
import { useStarredDashboardListQuery } from '../Dashboard/hooks/useDashboardListQuery';

const StarredDashboardList = () => {
  const { data: starredDashboard } = useStarredDashboardListQuery({
    enabled: true,
  });
  const wr = useWorkspaceRouter();
  const dashboards = starredDashboard?.dashboards || [];
  const emptySlots = Math.max(0, 5 - dashboards.length);

  if (!dashboards.length) return null;

  return (
    <VFlex g={0}>
      <Flex j="space-between" a="center">
        <Typo type="label2" weight="m" className="text-gray-700">
          Favorite dashboards
        </Typo>
        <Button
          variant="ghost_secondary"
          edgeAlign="right"
          rightIcon={<ChevronRight />}
          onClick={() => {
            wr.push('/dashboard2/list');
          }}
        >
          Dashboard
        </Button>
      </Flex>
      <SwiperContainer>
        <Swiper
          slidesPerView={'auto'}
          slidesPerGroup={3}
          spaceBetween={12}
          navigation={true}
          modules={[Navigation]}
          style={{
            width: '100%',
            overflowY: 'visible',
            padding: '16px 16px 16px 16px',
            margin: '0 -16px',
          }}
        >
          {dashboards.map((d) => (
            <SwiperSlide key={d.alias} style={{ width: 256 }}>
              <DashboardItem
                dashboard={d}
                onClick={async () => {
                  wr.push(`/dashboard2/${d.alias}`);
                }}
              />
            </SwiperSlide>
          ))}
          {Array.from({ length: emptySlots }).map((_, index) => (
            <SwiperSlide key={`empty-${index}`} style={{ width: 240 }}>
              <EmptyDashboardItem />
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperContainer>
    </VFlex>
  );
};

const EmptyDashboardItem = styled.div`
  height: 158px;
  border-radius: ${radius.token.lg}px;
  background-color: ${(props) => props.theme.colors.gray150};
`;

const SwiperContainer = styled.div`
  position: relative;
  .swiper-button-prev,
  .swiper-button-next {
    top: 0;
    margin-top: 0;
    width: 60px;
    height: 100%;
    transition: all 0.2s;
  }

  .swiper-button-next {
    right: 0;
    background: linear-gradient(
      to right,
      transparent,
      ${(props) => props.theme.colors.gray100}
    );
  }
  .swiper-button-prev {
    left: 0;
    background: linear-gradient(
      to left,
      transparent,
      ${(props) => props.theme.colors.gray100}
    );
  }
  .swiper-button-next svg,
  .swiper-button-prev svg {
    width: 14px;
    height: 14px;
    padding: 8px;
    background: ${(props) => props.theme.colors.gray100};
    border-radius: 50%;
    box-shadow: ${(props) => props.theme.shadow[1]};
    color: ${(props) => props.theme.colors.gray700};
    border: 1px solid ${(props) => props.theme.colors.gray400};
  }

  .swiper-button-next svg {
    margin-right: 2px;
  }
  .swiper-button-prev svg {
    margin-left: 2px;
  }

  .swiper-button-prev.swiper-button-disabled,
  .swiper-button-next.swiper-button-disabled {
    opacity: 0;
    cursor: auto;
    pointer-events: none;
  }
`;

export default StarredDashboardList;
